import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClientsService } from "../services/clients";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import {
  ROLE_CLIENT_ADMIN,
  ROLE_BUSINESS_DEVELOPMENT_LEAD,
  ROLE_SUPER_ADMIN,
  ROUTE_CLIENTS,
  ROUTE_FORBIDDEN_PAGE,
} from "../../SharedModule/utils/constants";
import { useNavigate } from "react-router";
import {
  IsLoadingKeyContacts,
  loadKeyContacts,
  SelectActualCompanyOwnerUserId,
  SelectActualRelationshipReviewId,
  SelectActualRelationshipReviewName,
  SelectActualRelationshipReviewStatus,
  SelectNeedUpdateKeyContactList,
  SelectOrderedByContacts,
  setActualRelationshipReviewId,
  setIsLoadingKeyContacts,
} from "../redux/reducers/clientKeyContacts.reducer";
import {
  IsLoadedContacts,
  IsLoadingContacts,
  loadContacts,
  SelectActualClientId,
  SelectClientContacts,
  setIsLoadingContacts,
  setNewUpdate,
} from "../redux/reducers/clientContacts.reducer";
import {
  IsClientsLoaded,
  SelectActualClientForHeaderInfo,
  SelectClientsIds,
  SelectOrderedBy,
  setActualClientForHeaderInfo,
  setIsActualClientForHeaderInfoLoading,
} from "../redux/reducers/clientsList.reducer";
import { Client } from "../types/clients-module-types";
import {
  IsApplySupervisorCheck,
  IsReviewsLoaded,
  loadCostCentersOwned,
  loadReviewsInfo,
  SelectCostCenterOwnedIds,
  SelectOrderedByReviewsList,
  setIsLoadingReviews,
} from "../redux/reducers/clientRelationshipReviewsList.reducer";
import { UsersService } from "../../UsersModule/services/users";
import { SelectCostCentersOwned } from "../../UsersModule/redux/reducers/usersList.reducer";
import {
  isSupervisor,
  verifyModuleAccess,
} from "../../SharedModule/utils/accessUtils";

import { useClientsList } from "./useClientsList";

const loadCostCentersIfSupervisor = async (
  isSupervisorInfo,
  notSuperOrClientAdmin,
  costCentersOwned,
  activeUserEmail,
  dispatch
) => {
  if (!isSupervisorInfo || !notSuperOrClientAdmin || costCentersOwned) {
    return;
  }
  let costCentersOwnedResponse;
  if (!costCentersOwned) {
    costCentersOwnedResponse = await UsersService.getCostCentersOwned(
      activeUserEmail
    );
  }
  dispatch(
    loadCostCentersOwned({
      costCentersOwned: costCentersOwned || costCentersOwnedResponse,
      applySupervisorCheck: true,
    })
  );
};

const loadHeaderInfoIfNeeded = async (
  clientId,
  clientForHeaderInfo,
  dispatch,
  navigate
) => {
  if (clientForHeaderInfo && clientForHeaderInfo.id === clientId) {
    return;
  }
  dispatch(setIsActualClientForHeaderInfoLoading(true));
  const headerInfo = await ClientsService.getClientModuleHeaderInfo(clientId)
    .then((response) => {
      if (!response.success) {
        dispatch(
          addToast({
            mode: "error",
            message: response.message,
          })
        );
        dispatch(setIsActualClientForHeaderInfoLoading(false));
        navigate(ROUTE_CLIENTS);
        return true;
      }
      return response.data;
    })
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message:
            "Something wrong happened while getting the client header information.",
        })
      );
      dispatch(setIsActualClientForHeaderInfoLoading(false));
      navigate(ROUTE_CLIENTS);
      return true;
    });
  dispatch(setActualClientForHeaderInfo(headerInfo));
};

const loadContactsIfNeeded = async (
  isLoadedContacts,
  clientForHeaderInfo,
  actualClientId,
  clientId,
  dispatch,
  navigate
) => {
  if (
    isLoadedContacts &&
    clientForHeaderInfo &&
    clientForHeaderInfo.id === clientId &&
    actualClientId === clientId
  ) {
    return;
  }
  dispatch(setIsLoadingContacts(true));
  const clientsContactsInfo = await ClientsService.getClientContacts(clientId)
    .then((data) => {
      dispatch(setNewUpdate(false));
      return data;
    })
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message: "Contacts not found",
        })
      );
      dispatch(setIsLoadingContacts(false));
      navigate(ROUTE_CLIENTS);
      return true;
    });
  dispatch(loadContacts(clientsContactsInfo));
};

const loadReviewsIfNeeded = async (reviewsLoaded, clients, dispatch) => {
  if (reviewsLoaded) {
    return;
  }
  dispatch(setIsLoadingReviews(true));
  const reviewsInfo = await ClientsService.getClientRelationshipReviews();
  dispatch(loadReviewsInfo({ data: reviewsInfo.data, clients }));
};

const loadKeyContactsIfNeeded = async (
  actualRelationshipReviewId,
  relationshipReviewId,
  needUpdateKeyContactList,
  clientId,
  dispatch,
  navigate
) => {
  if (
    actualRelationshipReviewId &&
    actualRelationshipReviewId === relationshipReviewId &&
    !needUpdateKeyContactList
  ) {
    return;
  }
  dispatch(setIsLoadingKeyContacts(true));
  dispatch(setActualRelationshipReviewId(relationshipReviewId));
  const keyContactsInfo = await ClientsService.getClientKeyContacts(
    relationshipReviewId
  )
    .then((data) => {
      if (!data.success || data.data?.clientId !== clientId) {
        dispatch(
          addToast({
            mode: "error",
            message: data.success
              ? "This relationship review is not present in this client"
              : "Client not found",
          })
        );
        dispatch(setIsLoadingKeyContacts(false));
        navigate(ROUTE_CLIENTS);
        return true;
      }
      return data;
    })
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message: "Key Contacts not found",
        })
      );
      dispatch(setIsLoadingKeyContacts(false));
      navigate(ROUTE_CLIENTS);
      return true;
    });
  dispatch(loadKeyContacts(keyContactsInfo));
};

export const useClientKeyContacts = (
  clientId: number,
  relationshipReviewId: number
) => {
  useClientsList();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userRolesList, rolesLoaded, activeUserEmail, activeUserId } =
    useSelector(selectUserRoles);
  const isLoadingKeyContacts = useSelector(IsLoadingKeyContacts);
  const actualRelationshipReviewId = useSelector(
    SelectActualRelationshipReviewId
  );
  const actualRelationshipReviewName = useSelector(
    SelectActualRelationshipReviewName
  );
  const actualCompanyOwnerUserId = useSelector(SelectActualCompanyOwnerUserId);

  const actualRelationshipReviewStatus = useSelector(
    SelectActualRelationshipReviewStatus
  );
  const { list: keyContactList } = useSelector(SelectOrderedByContacts);
  const { clientlist: clients } = useSelector(SelectOrderedBy);

  const clientContactsInfo = useSelector(SelectClientContacts);
  const actualClientId = useSelector(SelectActualClientId);
  const isLoadingContacts = useSelector(IsLoadingContacts);
  const isLoadedContacts = useSelector(IsLoadedContacts);
  const needUpdateKeyContactList = useSelector(SelectNeedUpdateKeyContactList);

  const isApplySupervisorCheck = useSelector(IsApplySupervisorCheck);
  const costCenterOwnedIds = useSelector(SelectCostCenterOwnedIds);
  const costCentersOwned = useSelector(SelectCostCentersOwned);
  const isSupervisorInfo = isSupervisor(userRolesList);
  const notSuperOrClientAdmin = !verifyModuleAccess(
    true,
    [ROLE_SUPER_ADMIN, ROLE_CLIENT_ADMIN],
    userRolesList
  );

  const isApplyEngagementLeadCheck =
    verifyModuleAccess(true, [ROLE_BUSINESS_DEVELOPMENT_LEAD], userRolesList) &&
    notSuperOrClientAdmin &&
    activeUserId === actualCompanyOwnerUserId;

  const reviewsLoaded = useSelector(IsReviewsLoaded);
  const { reviewsList: clientRelationshipReview } = useSelector(
    SelectOrderedByReviewsList
  );

  // To check is header info is already loaded
  const ClientForHeaderInfo: Client = useSelector(
    SelectActualClientForHeaderInfo
  );
  // to check if clientId is in list of clients that user could see
  const isClientsLoaded = useSelector(IsClientsLoaded);
  const clientsIds = useSelector(SelectClientsIds);

  const loadClientKeyContacts = useCallback(
    async (clientId) => {
      if (!rolesLoaded || !isClientsLoaded) {
        return;
      }
      // This check should go in all clients custom hooks
      if (!clientsIds.includes(clientId)) {
        dispatch(
          addToast({
            mode: "error",
            message: "You're not allowed to see this Client Page",
          })
        );
        navigate(ROUTE_FORBIDDEN_PAGE);
        return true;
      }
      // Load info when user is supervisor
      await loadCostCentersIfSupervisor(
        isSupervisorInfo,
        notSuperOrClientAdmin,
        costCentersOwned,
        activeUserEmail,
        dispatch
      );
      // Load info header when user put a new URL
      await loadHeaderInfoIfNeeded(
        clientId,
        ClientForHeaderInfo,
        dispatch,
        navigate
      );
      // get contacts for actual client
      await loadContactsIfNeeded(
        isLoadedContacts,
        ClientForHeaderInfo,
        actualClientId,
        clientId,
        dispatch,
        navigate
      );
      // need relationships to get costCenterId to check is user could add key contact
      await loadReviewsIfNeeded(reviewsLoaded, clients, dispatch);
      // page info
      await loadKeyContactsIfNeeded(
        actualRelationshipReviewId,
        relationshipReviewId,
        needUpdateKeyContactList,
        clientId,
        dispatch,
        navigate
      );
    },
    [dispatch, rolesLoaded, isClientsLoaded, ClientForHeaderInfo, isLoadedContacts, actualClientId, reviewsLoaded, actualRelationshipReviewId, needUpdateKeyContactList, isSupervisorInfo, costCentersOwned]
  );

  useEffect(() => {
    loadClientKeyContacts(clientId);
  }, [loadClientKeyContacts]);

  return {
    ClientForHeaderInfo,
    isLoadingKeyContacts,
    actualRelationshipReviewName,
    actualRelationshipReviewStatus,
    keyContactList,
    userRolesList,
    rolesLoaded,
    clientContactsInfo,
    isLoadingContacts,
    isApplySupervisorCheck,
    costCenterOwnedIds,
    clientRelationshipReview,
    isApplyEngagementLeadCheck,
  };
};
